import { useEffect, useMemo, useState } from 'react';
import type { NavGroupView, NavPageView } from '../../navigation/navConfig';
import { useReportCatalog } from '../../pages/reports/useReportCatalog';

/**
 * The Reports panel's Favorites group, drawn once and left still (Jeff, 2026-10-26: "very bouncy
 * loading the favorites on the second nav bar").
 *
 * The list needs two queries (the reports, the user's stars), the facility's settings and the
 * permission mode, which arrive one after another; each can add or drop a favorite, so drawing it
 * as they came in made the group appear, grow and shrink, pushing Analysis and Cost & Planning up
 * and down. Now the group waits until all four have answered, and until then shows the list this
 * user last saw at this facility (kept in this browser), which is nearly always the same list, so
 * nothing moves. The first visit on a browser has nothing kept and the group appears once, settled.
 *
 * The settings or the permission mode can fail to load; the group then draws anyway once the two
 * lists are in and SETTLE_CAP_MS has passed, rather than never.
 */

export const SETTLE_CAP_MS = 2000;
const STORAGE_PREFIX = 'dietech.sidebar.reportFavorites';

type FavoriteLink = Pick<NavPageView, 'label' | 'route' | 'key'>;

export function favoritesStorageKey(facilityId: number, userId: number): string {
  return `${STORAGE_PREFIX}.${facilityId}.${userId}`;
}

function readKept(key: string): FavoriteLink[] | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const ok = parsed.every((p) => p && typeof p.label === 'string' && typeof p.route === 'string' && typeof p.key === 'string');
    return ok ? (parsed as FavoriteLink[]) : null;
  } catch {
    return null;
  }
}

function keep(key: string, list: FavoriteLink[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(list));
  } catch {
    // Private window or blocked storage: the group just waits for the live list next time.
  }
}

function toGroup(list: FavoriteLink[] | null): NavGroupView | null {
  if (!list || list.length === 0) return null;
  return { title: 'Favorites', pages: list.map((p) => ({ ...p, description: '' })) };
}

export function useSidebarReportFavorites(): NavGroupView | null {
  const { facilityId, userId, favouriteRows, favoritesAnswered, favoritesSettled } = useReportCatalog();
  const storageKey = facilityId > 0 && userId > 0 ? favoritesStorageKey(facilityId, userId) : null;

  const live = useMemo<FavoriteLink[]>(
    () => [...favouriteRows]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((r) => ({ label: r.name, route: `/reports/run/${r.slug}`, key: `favorite:${r.slug}` })),
    [favouriteRows],
  );

  // The two lists are in but the settings or permission mode are not: stop waiting after the cap.
  const [waitedOut, setWaitedOut] = useState<string | null>(null);
  useEffect(() => {
    if (!storageKey || !favoritesAnswered || favoritesSettled) return undefined;
    const t = window.setTimeout(() => setWaitedOut(storageKey), SETTLE_CAP_MS);
    return () => window.clearTimeout(t);
  }, [storageKey, favoritesAnswered, favoritesSettled]);

  const final = favoritesSettled || (favoritesAnswered && waitedOut === storageKey);

  // Re-read when `final` drops (a facility switch), by which time the last live list was kept.
  const kept = useMemo(() => (storageKey && !final ? readKept(storageKey) : null), [storageKey, final]);

  useEffect(() => {
    if (storageKey && final) keep(storageKey, live);
  }, [storageKey, final, live]);

  return useMemo(() => toGroup(final ? live : kept), [final, live, kept]);
}
